import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, Heart, Sparkles, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';

const AboutPage = () => {
  const values = [
    {
      icon: Heart,
      title: 'Crafted with Love',
      description: 'Every piece is made with genuine passion and care, ensuring each creation is special.'
    },
    {
      icon: Sparkles,
      title: 'Unique & Personal',
      description: 'We believe in creating one-of-a-kind pieces that reflect your personality and story.'
    },
    {
      icon: Users,
      title: 'Customer First',
      description: 'Your satisfaction is our priority. We work closely with you to bring your vision to life.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Header */}
      <section className="bg-white py-12 md:py-16">
        <div className="container-handcraft">
          <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
            <Link to="/" className="hover:text-charcoal transition-colors">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-charcoal">About</span>
          </nav>
        </div>
      </section>

      {/* Hero */}
      <section className="section-spacing pt-0">
        <div className="container-handcraft">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <p className="font-handwritten text-3xl text-accent mb-4">Our Story</p>
              <h1 className="font-serif text-4xl md:text-5xl text-charcoal mb-6 leading-tight">
                Where Creativity Meets Craftsmanship
              </h1>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  YM's Creation was born from a simple belief: that handmade items carry a special
                  kind of magic. Each piece tells a story, holds memories, and brings warmth to
                  everyday moments.
                </p>
                <p>
                  What started as a passion project has grown into a studio dedicated to creating
                  beautiful, personalized handcrafted items. From aromatic candles to custom portraits,
                  from wedding trousseau to corporate gifts — we pour our heart into everything we make.
                </p>
                <p>
                  We believe that the best gifts are those made with intention and care. That's why
                  we offer both ready-made treasures and custom creations, ensuring there's something
                  special for every occasion and every person.
                </p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="aspect-[4/5] rounded-2xl overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1651204790676-c86edd9accd2?w=800&q=80"
                  alt="Handcraft workshop"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 w-48 h-48 bg-accent/20 rounded-2xl -z-10" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-spacing bg-white">
        <div className="container-handcraft">
          <div className="text-center mb-12">
            <p className="font-handwritten text-2xl text-accent mb-2">What Drives Us</p>
            <h2 className="font-serif text-3xl md:text-4xl text-charcoal">Our Values</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center p-8"
              >
                <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
                  <value.icon className="w-7 h-7 text-accent" />
                </div>
                <h3 className="font-serif text-xl mb-2">{value.title}</h3>
                <p className="text-muted-foreground text-sm">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="section-spacing">
        <div className="container-handcraft">
          <div className="text-center mb-12">
            <p className="font-handwritten text-2xl text-accent mb-2">How We Work</p>
            <h2 className="font-serif text-3xl md:text-4xl text-charcoal">Our Process</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Share Your Vision', desc: 'Tell us about your idea or choose from our collection' },
              { step: '02', title: 'Design & Plan', desc: 'We create a design plan tailored to your needs' },
              { step: '03', title: 'Craft with Care', desc: 'Our artisans bring your vision to life' },
              { step: '04', title: 'Deliver Joy', desc: 'Beautifully packaged and delivered to you' },
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative"
              >
                <span className="font-serif text-6xl text-accent/20">{item.step}</span>
                <h3 className="font-serif text-xl text-charcoal -mt-4 mb-2">{item.title}</h3>
                <p className="text-muted-foreground text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-spacing bg-charcoal text-white">
        <div className="container-handcraft text-center">
          <p className="font-handwritten text-3xl text-accent mb-4">Ready to Create?</p>
          <h2 className="font-serif text-3xl md:text-4xl mb-6">
            Let's Make Something Beautiful Together
          </h2>
          <p className="text-white/70 max-w-xl mx-auto mb-8">
            Whether you're looking for a ready-made treasure or have a custom project in mind,
            we're here to help you find the perfect handcrafted piece.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/shop">
              <Button className="btn-handcraft bg-accent text-white hover:bg-accent/90">
                Shop Now
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="btn-handcraft border-white text-white hover:bg-white hover:text-charcoal">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
